"use client";

import React, { ReactNode } from "react";
import { Tooltip as RadixTooltip } from "radix-ui";
import { useTheme } from "../ThemeProvider";
import { csx } from "../../utils";
import "../Dialog/Dialog.css";
import "./Tooltip.css";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";

export interface TooltipContentProps {
  content: string | ReactNode;
  arrow?: boolean;
  placement?: TooltipPlacement;
  "data-testid"?: string;
}

const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  ({ content, arrow, placement = "top", "data-testid": testId }, ref) => {
    const { style, className } = useTheme();

    return (
      <RadixTooltip.Content
        ref={ref}
        side={placement}
        className={csx("proton__Tooltip", "proton-Dialog", className)}
        style={style as React.CSSProperties}
        sideOffset={8}
        data-testid={testId}
      >
        {content}
        {arrow && (
          <RadixTooltip.Arrow
            className={csx("proton__Tooltip-arrow", className)}
            width={8}
            height={8}
          >
            <svg width={8} height={8} viewBox="0 0 8 8">
              <path d="M0 0 L4 4 L8 0" />
            </svg>
          </RadixTooltip.Arrow>
        )}
      </RadixTooltip.Content>
    );
  }
);

TooltipContent.displayName = "TooltipContent";

export interface TooltipProps {
  /**
   * Whether to show an arrow pointing to the tooltip.
   * @default true
   */
  arrow?: boolean;
  /**
   * The children to display in the tooltip.
   */
  children: React.ReactNode;
  /**
   * The content to display in the tooltip. Zero-length titles string are never displayed.
   */
  content: string | React.ReactNode;
  /**
   * A test ID.
   */
  "data-testid"?: string;
  /**
   * The placement of the tooltip.
   * @default "bottom"
   */
  placement?: TooltipPlacement;
  /**
   * Whether to disable the trigger click feature.
   * This is useful when using an info only Tooltip that might wrap a clickable element.
   */
  disableTriggerClick?: boolean;
  /**
   * Whether the tooltip is disabled.
   */
  isDisabled?: boolean;
  /**
   * Delay before showing the tooltip (in milliseconds).
   * @default 100
   */
  delay?: number;
  /**
   * Whether the tooltip is open by default.
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * The callback function to call when the tooltip is opened or closed.
   */
  onOpenChange?: (open: boolean) => void;
}

/**
 * A component that displays a tooltip when the user hovers over the trigger.
 * For mobile, use Popover instead.
 * API:
 *  - {@link TooltipProps}
 *
 * @example
 * ```tsx
    <Tooltipcontent={...} placement="top" arrow isDisabled={disabled}>
      {children}
    </Tooltip>
 * ```
 */
export function Tooltip({
  arrow = true,
  children,
  content,
  delay = 100,
  placement = "top",
  isDisabled,
  defaultOpen = false,
  onOpenChange,
  disableTriggerClick,
  "data-testid": testId,
}: TooltipProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const triggerClickRef = React.useRef<boolean>(false);
  const hasHovered = React.useRef<boolean>(false);

  if (content === "") return null;

  const handleOpenChange = (openState: boolean) => {
    // When opened via hover, we want to disable the trigger click feature as it causes
    // the tooltip state to get out of sync with the trigger state.
    if (openState && !triggerClickRef.current) hasHovered.current = true;

    if (onOpenChange) onOpenChange(openState);

    // If the tooltip is clicked, it manually calls setIsOpen, so we don't want to call
    // it again here.
    if (!triggerClickRef.current) setIsOpen(openState);
    triggerClickRef.current = false;
  };

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root
        open={isOpen}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        delayDuration={delay}
      >
        <RadixTooltip.Trigger asChild>
          {disableTriggerClick ? (
            <span aria-label="Show tooltip" className="proton__Tooltip-trigger">
              {children}
            </span>
          ) : (
            <div
              role="button"
              aria-label="Show tooltip"
              data-hovered={isOpen}
              onClick={() => {
                if (disableTriggerClick || isDisabled) return;

                // Once hovered it's safe to assume the user isn't trying to use via a touch device,
                // so we can safely disable the trigger click feature.
                if (hasHovered.current) return;

                triggerClickRef.current = true;
                setIsOpen(!isOpen);
              }}
              className="proton__Tooltip-trigger"
            >
              {children}
            </div>
          )}
        </RadixTooltip.Trigger>
        {!isDisabled && (
          <RadixTooltip.Portal>
            <TooltipContent
              content={content}
              placement={placement}
              arrow={arrow}
              data-testid={testId}
            />
          </RadixTooltip.Portal>
        )}
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

Tooltip.displayName = "Tooltip";
